/**
 * Form Component
 *
 */
"use strict";

import axios from "axios";
import Qs from "qs";
import Pristine from "pristinejs/dist/pristine.min.js";
import { UTM } from "./utm.js";
import { Mutiny } from "./mutiny.js";
import { TrustedForm } from "./trusted-form.js";

export class AmericorForm
{  
  /**
   * Wrapper
   * 
   * @var object
   */
  static wrapper = document.querySelector(".americor-form");

  /**
   * Default Vendor ID
   *
   * @var int
   */
  static vid;

  /**
   * Define form field querySelectors
   *
   * Key should match Lead API field name return to properly validate fields
   *
   * @see https://admin.americor.com/swagger/doc
   * @var object
   */
  static form_fields = {
    general: 'div.general-error',
    offer: "input[name=offer_code]",
    debt: "select[name=debt]",
    firstName: "input[name=firstName]",
    lastName: "input[name=lastName]",
    phone: "input[name=phone]",
    email: "input[name=email]",
    street: "input[name=street]",
    city: "input[name=city]",
    zip: "input[name=zip]",
    credit_authorization: "input[name=credit_authorization]",
  };

  /**
   * Default Pristine form validation config
   *
   * Change this to match form markup
   *
   * @see https://pristine.js.org/
   * @var object
   */
  static form_validation_config = {
    classTo: "form-group",
    errorClass: "has-error",
    successClass: "has-success",
    errorTextParent: "form-group",
    errorTextTag: "div",
    errorTextClass: "help-block-error",
  };

  /**
   * Lead UUID
   *
   * @var string
   */
  static uuid = localStorage.getItem("lead_uuid") ?? null;

  /**
   * UTM Helper
   *
   * @var UTM
   */
  static utm = new UTM();

  /**
   * Mutiny object
   *
   * @var Mutiny
   */
  static mutiny = new Mutiny();

  /**
   * Trusted Form Helper
   *
   * @var TrustedForm
   */
  static trusted_form = new TrustedForm();

  /**
   * Constructor
   *
   * @returns
   */
  constructor()
  {
    this.mask_phone();
    this.init_form();
  }

  /**
   * Add phone masking
   * 
   */
  mask_phone()
  {
    const phone = document.querySelector('input[type=tel], input[name=phone]');
    
    phone?.addEventListener('input', ( e ) => {
      var x = e.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
    });
  }

  /**
   * Init all forms
   * 
   */
  init_form()
  {
    const _self = this;
    const forms = document.querySelectorAll('form.americor-form');

    if ( ! forms ) return;

    forms.forEach(( form ) => {
      var is_in_modal = form.dataset.isInModal ?? false;

      const pristine    = new Pristine(form, AmericorForm.form_validation_config);
      const back        = form.querySelector('button.back');
      const prevStep    = ( form.dataset.previousStep && form.dataset.previousStep != '' ) ? form.dataset.previousStep : null;
      const currentStep = ( form.dataset.currentStep && form.dataset.currentStep != '' ) ? form.dataset.currentStep : null;

      back?.addEventListener('click', ( event ) => {
        document.querySelector('.' + currentStep).classList.remove('active', 'default');
        document.querySelector('.' + prevStep).classList.add('active');
      });

      form.addEventListener("submit", function ( event ) {
        event.preventDefault();

        const _el        = this;
        const form_details = {
          channel: ( form.dataset.channel && form.dataset.channel != '' ) ? form.dataset.channel : null,
          company: ( form.dataset.company && form.dataset.company != '' ) ? form.dataset.company : null,
          currentStep: currentStep,
          isCompleted: ( form.dataset.isCompleted && form.dataset.isCompleted != '' ) ? form.dataset.isCompleted : false,
          leadAction: form.dataset.leadAction ?? 'create_lead',
          nextStep: ( form.dataset.nextStep && form.dataset.nextStep != '' ) ? form.dataset.nextStep : null,
          populateWithResponse: form.dataset.populateWithResponse ?? false,
          prevStep: prevStep,
          pristine: pristine,
          redirectUrl: ( form.dataset.redirectUrl && form.dataset.redirectUrl != '' ) ? form.dataset.redirectUrl : null,
          skipSubmit: ( form.dataset.skipSubmit && form.dataset.skipSubmit == 'true' ) ? true : false,
          vendorId: _self._get_vendor_id( form ),
        }
  
        console.log(form_details);
        
        // Validate form
        if ( ! form_details.pristine.validate() ) return;

        // Skip submit to API
        if ( form_details.skipSubmit == true )
        {
          // Redirect URL
          if ( form_details.redirectUrl ) window.location.href = form_details.redirectUrl;

          // Toggle next step
          else if ( form_details.nextStep ) _self._move_to_next_step( _el, form_details );
        }

        // Submit form to API
        else
        {
          _self._submit( form, form_details, event, ( response ) => {
            if ( ! is_in_modal )
            {
              // Close micromodal if open
              const modal = document.querySelector('.modal');

              if ( modal !== null && modal.classList.contains('is-open') )
              {
                $('body').removeClass('modal-opened');

                MicroModal.close( modal.id );
              }
            }
            
            // Completed
            if ( form_details.isCompleted  )
            {
              if ( response.callSchedulerLink )
              {
                window.location.href = response.callSchedulerLink;
              }
              else
              {
                window.location.href = form_details.redirectUrl ?? '/thank-you/';
              }
            }

            // Redirect URL
            else if ( form_details.redirectUrl )
            {
              window.location.href = form_details.redirectUrl;
            }

            // Toggle next step
            else if ( form_details.nextStep )
            {
              // If populating form with response, set values
              if ( form_details.populateWithResponse )
              {
                for ( let [ key,value ] of Object.entries(response) )
                {
                  const fields = document.querySelectorAll('[name=' + key + ']');
                  
                  fields.forEach(( field ) => field.value = value );
                }
              }

              _self._fire_data_layer_event( form_details, response );
              _self._move_to_next_step( _el, form_details );
            }
          });
        }
      });

      form.dispatchEvent( new CustomEvent('form:init', {
        bubbles: true,
        detail: {
          form: form
        }
      } ) );
    });
  }

  /**
   * Submit form data to API
   *
   * @param object    form
   * @param object    form_details
   * @param event     event
   * @param function  callback
   * @return Promise
   */
  async _submit( form, form_details, event, callback )
  {
    form.classList.add("loading");

    const _self     = this;
    const formData  = new FormData(form);
    const form_data = Object.fromEntries(formData);

    // Set vendor ID
    form_data.vendorId = form_details.vendorId;
    form_data.params   = {};

    /**
     * Check for submit button value and name
     * 
     * Use case would be if form submits on button click and value is on the button itself
     * 
     * Name should be params[<key>]
     */
    const button_value = event.submitter.value;
    const button_name  = event.submitter.name;

    if ( button_value && button_name )
    {
      let params_key = button_name.replace('params[', '').replace(']', '');

      form_data.params[params_key] = button_value;

      delete form_data.button_name;
    }

    /**
     * Check for custom params arrays
     * 
     * Use case would be checkboxes with multiple values
     * 
     * Name should be params[<key>]
     */
    for ( const key in form_data )
    {
      if ( key.startsWith('params[') )
      {
        let params     = formData.getAll(key).toString();
        let params_key = key.replace('params[', '').replace(']', '');

        form_data.params[params_key] = params;

        delete form_data[key];
      }
    }

    // Set params
    form_data.params = { ...form_data.params, ...this._get_parameters() };

    // If creating lead, add trusted form token
    if ( form_details.leadAction == 'create_lead' )
      form_data.trusted_form_token = await AmericorForm.trusted_form.get_token();

    // If updating lead, add UUID
    if ( form_details.leadAction == 'update_lead' )
      form_data.uuid = AmericorForm.uuid;

    if ( form_details.leadAction == 'offer_code' ) {}

    // Add channel and company
    if ( form_details.channel ) form_data.channel = form_details.channel;
    if ( form_details.company ) form_data.company = form_details.company;

    // Add isCompleted status
    if ( form_details.isCompleted ) form_data.isCompleted = true;

    // Dispatch beforeSubmit event
    form.dispatchEvent( new CustomEvent('form:beforeSubmit', {
      bubbles: true,
      detail: {
        form: form,
        form_details: form_details,
        form_data: form_data,
      }
    } ) );

    console.table(form_data);

    // Submit lead to API
    let submit = this._make_api_request( form_data, form_details.leadAction );

    submit.then( ( response ) => {
      form.classList.remove("loading");

      // Dispatch afterSubmit event
      form.dispatchEvent( new CustomEvent('form:afterSubmit', {
        bubbles: true,
        detail: {
          form: form,
          form_details: form_details,
          form_data: form_data,
          response: response
        }
      } ) );
      
      console.table(response);

      // Validate response
      if ( ! response || ! _self._validate_response( response, form_details.pristine ) ) return;

      // Set Lead UUID
      if ( response.uuid )
      {
        AmericorForm.uuid = response.uuid;

        localStorage.setItem("lead_uuid", response.uuid);
      }

      _self._fire_data_layer_event( form_details, response );

      callback(response);
    });
  }

  /**
   * Make request to API
   *
   * @param object data
   * @param string action
   * @return Promise
   */
  async _make_api_request( data, action )
  {
    let params = data.params || {};

    try {
      const response = await axios.post( americor.ajaxurl, Qs.stringify(
        {
          action: action,
          content: data,
          params: params
        }),
      );

      return response["data"];
    }
    catch ( error )
    {
      console.log(error.response);
    }
  }

  /**
   *
   * Validate API response and display error messages
   *
   * @param object response
   * @param object pristine - Pristine instance to trigger callback error validation
   * @returns boolean
   */
  _validate_response( response, pristine )
  {
    if ( "status" in response && response.status == 400 )
    {
      if ( response.errors )
      {
        for ( const key in response.errors )
        {
          const element = document.querySelector(AmericorForm.form_fields[key]);

          if ( key == 'general' && element )
          {
            element.innerHTML = '<div class="pristine-error help-block-error" role="alert">' + response.errors[key] + '</div>';
          }
          else
          {
            pristine.addError(element, response.errors[key]);
          }
        }
      }

      return false;
    }

    const general = document.querySelector(AmericorForm.form_fields['general']);

    if ( general ) general.innerHTML = '';

    return true;
  }

  /**
   * Fire data layer event
   *
   * @param Object form_details
   * @param Object response
   */
  _fire_data_layer_event( form_details, response )
  {
    if ( ! form_details || ! response ) return;

    window.dataLayer = window.dataLayer || [];
    
    if ( form_details.leadAction == 'offer_code' )
    {
      window.dataLayer.push({
        event: "mailer_code",
        lead_id: AmericorForm.uuid,
      });
    }

    if ( form_details.leadAction == 'create_lead' ||
       ( form_details.leadAction == 'update_lead' && ! form_details.isCompleted ) )
    {
      window.dataLayer.push({
        event: "leadInfo",
        lead_id: AmericorForm.uuid,
      });
    }

    if ( form_details.isCompleted )
    {
      window.dataLayer.push({
        event: "leadGenerated",
        lead_id: response.uuid,
        firstName: response.firstName,
        lastName: response.lastName,
        phoneNumber: response.phone,
        leadStatus: response.quality,
        email: response.email,
        city: response.city,
        zip: response.zip,
        debt_amount: response.debt,
        credit_pull: ( (response.debtCreditReport) ? parseFloat(response.debtCreditReport) : 0.00 )
      });
    }
  }

  /**
   * Move to next step
   * 
   * Dispatches CustomEvent with current and next step classnames
   * 
   * @param object form
   * @param object form_details
   * @return void
   */
  _move_to_next_step( form, form_details ) {
    form.dispatchEvent( new CustomEvent('form:stepChange', {
      bubbles: true,
      detail: {
        form: form,
        form_details: form_details
      }
    } ) );

    const nextStep    = document.querySelector('.' + form_details.nextStep);
    const currentStep = document.querySelector('.' + form_details.currentStep);
    const progress    = document.getElementById("progress-bar");

    currentStep?.classList.remove('active', 'default');
    nextStep?.classList.add('active');
    
    if ( progress ) progress.style.width = nextStep.dataset.progress;
  }

  /**
   * Get Vendor ID from URL
   *
   * @returns int
   */
  _get_vendor_id( form ) {
    const urlParams = new URLSearchParams(window.location.search);

    return ( urlParams.has("vid") ) ? parseInt( urlParams.get("vid") ) : parseInt( form.dataset.vendorId );
  }

  /**
   * Get parameters with UTM and Mutiny values
   *
   * @returns object
   */
  _get_parameters() {
    return {
      ...AmericorForm.utm.get_parameters(),
      ...AmericorForm.mutiny.get_parameters(),
    };
  }
}
